import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { ArcadiaHeaderLogo } from '@/components/layout/ArcadiaHeaderLogo';
import { ArcImage as Image } from '@/components/ui/arc-image';
import { MaterialSymbol } from '@/components/ui/material-symbol';
import { useState, useEffect } from 'react';
import { Coins, LogOut, User, Settings, ShieldCheck } from 'lucide-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Button } from '@/components/ui/button';
import type { Profile } from '@/types';

const mainNav = [
  { href: '/store', label: 'Store', match: (p: string) => p === '/store' || p.startsWith('/store/') },
  { href: '/library', label: 'Library', match: (p: string) => p.startsWith('/library') },
  { href: '/community', label: 'Community', match: (p: string) => p.startsWith('/community') },
  { href: '/developer', label: 'Developer', match: (p: string) => p.startsWith('/developer') },
];

interface TopPrimaryBarProps {
  profile: Profile | null;
  /** Friends / social affordance (desktop); rendered in header actions cluster */
  friendsSlot?: React.ReactNode;
}

function AccountMenu({ profile, isMobile }: { profile: Profile; isMobile: boolean }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open account menu"
          className={cn(
            'flex touch-target items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-white/5',
            isMobile && 'px-1.5'
          )}
        >
          <span className="inline-flex h-8 w-8 flex-shrink-0 overflow-hidden rounded-full border border-white/20">
            {profile.avatarUrl ? (
              <Image
                src={profile.avatarUrl}
                alt={`${profile.displayName} avatar`}
                width={32}
                height={32}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-xs text-arc-text-muted">
                {profile.displayName[0]}
              </span>
            )}
          </span>
          {!isMobile && (
            <span className="max-w-[7rem] truncate text-sm font-medium text-arc-text-main sm:max-w-[10rem] lg:max-w-[14rem]">
              {profile.displayName}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" collisionPadding={12}>
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <User className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        {profile.isAdmin ? (
          <DropdownMenuItem asChild>
            <Link to="/admin/games">
              <ShieldCheck className="mr-2 h-4 w-4" />
              Admin · Game review
            </Link>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopPrimaryBar({ profile, friendsSlot }: TopPrimaryBarProps) {
  const { pathname } = useLocation();
  const { signOut } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const check = () => setCanGoBack(window.history.length > 1);
    check();
    window.addEventListener('popstate', check);
    return () => window.removeEventListener('popstate', check);
  }, [pathname]);

  const runSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      setTimeout(() => window.location.reload(), 100);
    } finally {
      setIsSigningOut(false);
    }
  };

  const navLinkClass = (href: string) => {
    const item = mainNav.find((n) => n.href === href);
    const active = item ? item.match(pathname) : false;
    return cn(
      'font-display text-sm font-medium tracking-tight transition-colors border-b-2 pb-1',
      active
        ? 'border-nav-active text-nav-active'
        : 'border-transparent text-slate-400 hover:text-slate-200'
    );
  };

  return (
    <header
      className={cn(
        'arc-header-surface fixed left-0 right-0 top-0 z-50 flex h-16 min-w-0 items-center justify-between gap-3 overflow-x-auto overflow-y-visible px-4 safe-top md:gap-6 md:px-8 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/20'
      )}
      aria-label="Arcadia"
    >
      <div className="flex min-w-0 flex-1 items-center gap-6 md:gap-8">
        <Link
          to="/"
          className="flex flex-shrink-0 items-center gap-2 hover:opacity-90"
          aria-label="Arcadia home"
        >
          <ArcadiaHeaderLogo />
        </Link>

        <nav className="hidden min-w-0 items-center gap-6 md:flex" aria-label="Primary">
          {mainNav.map((item) => (
            <Link key={item.href} to={item.href} className={navLinkClass(item.href)}>
              {item.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="flex flex-shrink-0 flex-nowrap items-center justify-end gap-2 md:gap-4">
        <div className="hidden flex-shrink-0 items-center gap-1 lg:flex" role="group" aria-label="Browser history">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-arc-text-muted hover:text-nav-active"
            onClick={() => window.history.length > 1 && window.history.back()}
            disabled={!canGoBack}
            title="Back"
            aria-label="Go back"
          >
            <MaterialSymbol name="arrow_back" className="!text-xl" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-400 hover:text-nav-active"
            onClick={() => window.history.forward()}
            title="Forward"
            aria-label="Go forward"
          >
            <MaterialSymbol name="arrow_forward" className="!text-xl" />
          </Button>
        </div>

        {friendsSlot}

        {profile ? (
          <div className="touch-target flex-shrink-0">
            <NotificationDropdown userId={profile.id} />
          </div>
        ) : null}

        {profile ? (
          <Link
            to="/settings"
            className="hidden touch-target flex-shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-sm text-arc-text-main transition-colors hover:bg-white/10 sm:flex"
            title="Arcadia Points balance"
          >
            <Coins className="h-4 w-4 text-amber-400/90" aria-hidden />
            <span className="font-medium tabular-nums">{(profile.arcadiaPointsBalance ?? 0).toLocaleString()}</span>
            <span className="text-arc-text-muted">pts</span>
          </Link>
        ) : null}

        {profile ? (
          <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isSigningOut}
              className="touch-target h-9 shrink-0 gap-1.5 border-arc-danger/40 bg-arc-danger/10 px-2 text-arc-danger hover:bg-arc-danger/20 hover:text-arc-danger sm:px-3"
              title={isSigningOut ? 'Signing out…' : 'Sign out'}
              aria-label={isSigningOut ? 'Signing out' : 'Sign out'}
              onClick={() => void runSignOut()}
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="font-display text-sm font-medium">
                {isSigningOut ? '…' : 'Sign out'}
              </span>
            </Button>
            <AccountMenu profile={profile} isMobile={isMobile} />
          </div>
        ) : (
          <Link to="/login">
            <Button variant="secondary" className="min-w-[120px]">
              Login / Sign up
            </Button>
          </Link>
        )}
      </div>
    </header>
  );
}
